// Angular imports
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// Services imports
import { MPCacheRegistrationService } from '../../../../_services/shared-services/mp-cache-registration-service';

// Constants imports
import { CacheDuration } from '../../../../_constants/cache-duration';
import { URLConstants } from '../../../../_constants/url-constants';

// Model imports
import { ARPaymentParameterDto } from '../../_models/account-receivable/ar-payment/ar-payment-parameter-dto';
import { ARPaymentResultDto } from '../../_models/account-receivable/ar-payment/ar-payment-result-dto';
import { ARPaymentAttachmentParameterDto } from '../../_models/account-receivable/ar-payment/ar-payment-attachment-parameter-dto';
import { ARReversePaymentEditorResultDto } from '../../_models/account-receivable/ar-payment/ar-reverse-payment-editor-result-dto';
import { ARReversePaymentRawDto } from '../../_models/account-receivable/ar-payment/ar-reverse-payment-raw-dto';
import { ARPaymentEditorResultDto } from '../../_models/account-receivable/ar-payment/ar-payment-editor-result-dto';
import { ARPaymentRawDto } from '../../_models/account-receivable/ar-payment/ar-payment-raw-dto';
import { ARPaymentTransferEditorResultDto } from '../../_models/account-receivable/ar-payment/ar-payment-transfer-editor-result-dto';
import { ARPaymentTransferRawDto } from '../../_models/account-receivable/ar-payment/ar-payment-transfer-raw-dto';
import { ARPaymentIssueRefundEditorResultDto } from '../../_models/account-receivable/ar-payment/ar-payment-issue-refund-editor-result-dto';
import { ARPaymentIssueRefundRawDto } from '../../_models/account-receivable/ar-payment/ar-payment-issue-refund-raw-dto';
import { TransactionLogResultDto } from '../../_models/common/transaction-log-result-dto';
import { PaymentTransferHistoryResultDto } from '../../_models/account-receivable/ar-payment/payment-transfer-history-result-dto';

@Injectable({
    providedIn: 'root'
})
export class ARPaymentService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getARPaymentDetail(param: ARPaymentParameterDto): Observable<ARPaymentResultDto> {
        const url = URLConstants.ccCBARPaymentApiUrl + `/detail${param.getQueryString()}`;
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(ARPaymentResultDto, response)));
    }

    getARPaymentTransferHistory(param: ARPaymentParameterDto): Observable<PaymentTransferHistoryResultDto[]> {
        const url = URLConstants.ccCBARPaymentApiUrl + `/transferhistory${param.getQueryString()}`;
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(PaymentTransferHistoryResultDto, response)));
    }

    getARPaymentTransactionHistory(transactionId: number, clearCache: boolean): Observable<TransactionLogResultDto[]> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/history?transactionId=${transactionId}&clearCache=${clearCache}`;
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        this.cacheRegistrationService.registerForCache(url, clearCache);
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(TransactionLogResultDto, response)));
    }

    getARPaymentCheckImage(param: ARPaymentParameterDto): Observable<Blob> {
        const url = URLConstants.ccCBARPaymentApiUrl + `/checkimage${param.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getARPaymentDocument(param: ARPaymentParameterDto): Observable<Blob> {
        const url = URLConstants.ccCBARPaymentApiUrl + `/document${param.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getARPaymentAttachment(param: ARPaymentAttachmentParameterDto): Observable<Blob> {
        const url = URLConstants.ccCBARPaymentApiUrl + `/attachment${param.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getARReversePaymentEditor(param: ARPaymentParameterDto): Observable<ARReversePaymentEditorResultDto> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/reverse/editor${param.getQueryString()}`;
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(ARReversePaymentEditorResultDto, response)));
    }

    addARReversePayment(params: ARReversePaymentRawDto): Observable<any> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/reverse`;
        return this.httpClient.post(url, classToPlain(params)).pipe(map((response: any) => response));
    }

    getARPaymentEditor(param: ARPaymentParameterDto): Observable<ARPaymentEditorResultDto> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/editor${param.getQueryString()}`;
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(ARPaymentEditorResultDto, response)));
    }

    updateARPaymentDetails(params: ARPaymentRawDto): Observable<any> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}`;
        return this.httpClient.put(url, classToPlain(params)).pipe(map((response: any) => response));
    }

    getARPaymentTransferEditor(param: ARPaymentParameterDto): Observable<ARPaymentTransferEditorResultDto> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/transfer/editor${param.getQueryString()}`;
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(ARPaymentTransferEditorResultDto, response)));
    }

    updateARPaymentTransfer(params: ARPaymentTransferRawDto): Observable<any> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/transfer`;
        return this.httpClient.put(url, classToPlain(params)).pipe(map((response: any) => response));
    }

    getARIssueRefundPaymentEditor(param: ARPaymentParameterDto): Observable<ARPaymentIssueRefundEditorResultDto> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/refund/editor${param.getQueryString()}`;
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(ARPaymentIssueRefundEditorResultDto, response)));
    }

    updateARIssueRefundPaymentEditor(params: ARPaymentIssueRefundRawDto): Observable<any> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}/refund`;
        return this.httpClient.put(url, classToPlain(params));
    }

    addARPaymentDetails(params: ARPaymentRawDto): Observable<any> {
        const url = `${URLConstants.ccCBARPaymentApiUrl}`;
        return this.httpClient.post(url, classToPlain(params));
    }

    deleteARPayment(param: ARPaymentParameterDto): Observable<any> {
        const url = URLConstants.ccCBARPaymentApiUrl;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(param)
        };
        return this.httpClient.delete<any>(url, options);
    }
}
